import type { ReactNode } from 'react'
import type { Device } from '@/api'
import { useAction, useDevices } from '@/api/hooks'
import { copy } from '@/copy'
import { Cell, Heads, SignOutRow } from '@/components/Rows'
import { PageHead } from '@/components/Shell'
import { Failed, Loading } from '@/components/States'
import { useConsole } from '@/lib/store'
import { day, lastUsed } from '@/lib/time'
import { useNow } from '@/lib/useNow'
import { useTitle } from '@/lib/useTitle'
import { Commands, InstallLink, Start, Step, Steps } from '@/components/Commands'
import { Terminal } from '@/components/Terminal'

const columns = 'min-[768px]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_72px_minmax(150px,auto)]'

// Devices: the computers signed in to the account (D-014). Signing one out
// is ADR-0006's revocation, and stops its shares; the word "installation" is
// never shown.
export function Devices() {
  useTitle(copy.titles.devices)
  const devices = useDevices()
  const now = new Date(useNow(30_000))
  const announce = useConsole((s) => s.announce)
  const signOut = useAction((api, csrf, id: string) => api.signOutDevice(csrf, id))

  const confirm = (d: Device) => async () => {
    try {
      await signOut.mutateAsync(d.id)
      announce(copy.devices.signedOut(d.name))
      return true
    } catch {
      return false
    }
  }

  const list = devices.data?.devices
  let body: ReactNode
  if (!list && devices.isError) body = <Failed what={copy.devices.failed} onRetry={() => void devices.refetch()} busy={devices.isFetching} />
  else if (!list) body = <Loading rows={2} />
  else if (list.length === 0)
    body = (
      <Start heading={copy.devices.empty} line={copy.devices.emptyLine}>
        <Steps>
          <Step n={1} title={copy.start.installStep} line={copy.start.installLine}>
            <InstallLink />
          </Step>
          <Step n={2} title={copy.start.loginStep} line={copy.start.loginLine}>
            <div className="max-w-[520px]">
              <Terminal command={copy.commands.login} />
            </div>
          </Step>
        </Steps>
      </Start>
    )
  else
    body = (
      <section className="card overflow-hidden" aria-label={copy.devices.heading}>
        <Heads
          columns={columns}
          labels={[{ text: copy.devices.device }, { text: copy.devices.signedIn }, { text: copy.devices.lastUsed }, { text: copy.devices.shares, numeric: true }]}
        />
        <ul className="m-0 list-none p-0">
          {list.map((d) => (
            <SignOutRow
              key={d.id}
              name={d.name}
              columns={columns}
              action={copy.devices.signOut}
              ask={copy.devices.ask(d.name, d.shares)}
              failedLine={copy.devices.failedAction}
              onConfirm={confirm(d)}
            >
              <h2 className="m-0 min-w-0 text-[17px] font-medium leading-6 [overflow-wrap:anywhere]">{d.name}</h2>
              <Cell label={copy.devices.signedIn}>{day(new Date(d.signed_in_at), now)}</Cell>
              <Cell label={copy.devices.lastUsed}>
                {d.last_used_at ? lastUsed(new Date(d.last_used_at), now, copy.devices.today) : <span className="text-secondary">{copy.devices.never}</span>}
              </Cell>
              <Cell label={copy.devices.shares} numeric>
                {d.shares}
              </Cell>
            </SignOutRow>
          ))}
        </ul>
      </section>
    )

  return (
    <>
      <PageHead label={list && list.length > 0 ? copy.devices.count(list.length) : undefined} title={copy.devices.heading} lead={copy.devices.lead} />
      {body}
      {list && list.length > 0 && (
        <Commands
          heading={copy.devices.more}
          line={copy.devices.moreLine}
          commands={[
            { label: copy.devices.addCommand, command: copy.commands.login },
            { label: copy.devices.listCommand, command: copy.commands.devices },
          ]}
        />
      )}
    </>
  )
}
